import * as React from 'react';
import { Formik, FormikErrors } from 'formik';
import { Button, Typography, TextField, styled } from '@mui/material';
import { useNewPasswordChallengeMutation } from 'src/redux/services/auth';
import PasswordFormField from '../components/PasswordFormField';
import FidoLogo from 'src/frontend-common/components/Logo';
import ForgotPassword from 'src/frontend-common/components/Icons/ForgotPassword';
import Column from 'src/frontend-common/components/Column';
import { Subtitle, Title } from './styledFormComponents';
import * as Yup from 'yup';

const Container = styled('div')(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(5),
  width: '50%',
  alignSelf: 'center',
  [theme.breakpoints.up('xl')]: {
    maxWidth: '1002.5px',
    padding: theme.spacing(15),
  },
}));

const CustomTextField = styled(TextField)(() => ({
  marginTop: 40,
  width: '100%',
}));

const Input = styled('div')(() => ({
  marginTop: 40,
  width: '100%',
}));

const CustomButton = styled(Button)(() => ({
  height: 56,
  marginTop: 60,
  width: '100%',
}));

interface FormValues {
  username: string;
  new_password: string;
  old_password: string;
}

interface PropTypes {
  text?: string;
  initialValues: {
    username: string;
    password: string;
  };
  onChallengeComplete: (token: string) => void;
}

const Spacer = styled(Column)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const ForceChangePasswordForm: React.FC<PropTypes> = ({ initialValues, text, onChallengeComplete }) => {
  const [newPasswordChallenge, { isLoading, isError }] = useNewPasswordChallengeMutation();

  const DisplayingErrorMessagesSchema = Yup.object().shape({
    username: Yup.string().required('This field is required'),
    new_password: Yup.string().required('This field is required'),
    old_password: Yup.string().required('This field is required'),
  });

  return (
    <Container>
      <div style={{ marginBottom: 50 }}>
        <FidoLogo />
      </div>
      <div>
        <div style={{ marginBottom: 24 }}>
          <ForgotPassword />
        </div>
        <Spacer centered>
          <Title style={{ fontSize: 40, fontWeight: 600, color: 'black' }}>{text || 'Forgot password?'}</Title>
          <Subtitle>
            <Typography
              style={{
                fontSize: 14,
                fontWeight: 400,
                color: '#616161',
              }}
            >
              Lost your password? Please enter your username or email address.
              <br />
              You will receive a link to reset your password via email.
            </Typography>
          </Subtitle>
        </Spacer>
        <Formik
          initialValues={{
            username: initialValues.username,
            old_password: initialValues.password,
            new_password: '',
            __all: '',
          }}
          validateOnChange={true}
          validationSchema={DisplayingErrorMessagesSchema}
          onSubmit={async (values, { setErrors }) => {
            const result = await newPasswordChallenge({
              username: values.username,
              old_password: values.old_password,
              new_password: values.new_password,
            });

            if ('error' in result && 'data' in result.error) {
              const violations = // @ts-ignore:next-line
                result.error?.data?.violations as FormikErrors<FormValues>;
              setErrors({ ...violations });
              return;
            }

            if ('data' in result) {
              onChallengeComplete(result.data.token);
            }
          }}
        >
          {(formikProps) => {
            const {
              errors: { username: usernameError, old_password: oldPasswordError, new_password: newPasswordError, __all: genericErrors },
              touched: { username: usernameTouched, old_password: oldPasswordTouched, new_password: newPasswordTouched },
            } = formikProps;

            return (
              <>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    formikProps.handleSubmit();
                  }}
                >
                  <CustomTextField
                    variant="outlined"
                    label="Email address"
                    error={usernameTouched && !!usernameError}
                    helperText={usernameTouched && usernameError}
                    disabled
                    {...formikProps.getFieldProps('username')}
                  />
                  <CustomTextField
                    variant="outlined"
                    label="Old password"
                    type="password"
                    error={oldPasswordTouched && !!oldPasswordError}
                    helperText={oldPasswordTouched && oldPasswordError}
                    {...formikProps.getFieldProps('old_password')}
                  />
                  <Input>
                    <PasswordFormField
                      label="New password"
                      passwordError={newPasswordError}
                      passwordTouched={newPasswordTouched}
                      showStrength
                      requirements={true}
                      fieldProps={formikProps.getFieldProps('new_password')}
                    />
                  </Input>
                </form>

                {isError && (
                  <Typography style={{ marginTop: 16 }} variant="caption" color="error">
                    {genericErrors}
                  </Typography>
                )}

                <CustomButton
                  variant="contained"
                  style={{ backgroundColor: 'black', color: 'white' }}
                  disabled={isLoading}
                  onClick={() => formikProps.submitForm()}
                >
                  {isLoading ? 'Confirming...' : 'Confirm'}
                </CustomButton>
              </>
            );
          }}
        </Formik>
      </div>
    </Container>
  );
};

export default ForceChangePasswordForm;
